/**
 * Free-gift ATC modal (FRI-247).
 *
 * Subscribes to ajaxProduct:added. When the added variant matches a gift
 * rule with `show_modal_on_atc: true` (and the variant is in the rule's
 * trigger_variant_ids / conditions.items), opens a modal with one or
 * more size pickers. Claim batches the selections in one POST
 * /cart/add.js with sections, then publishes theme.pubsub.cartUpdate
 * so the existing cart-drawer subscriber refreshes the drawer.
 *
 * Slot count comes from a separate flag:
 *   - per_qualifying_unit: true → trigger_qty − already_claimed (kit)
 *   - otherwise              → 1 slot, hidden if already claimed
 *
 * Reads the same #CartGiftRules + #CartGiftVariantMap JSON as the
 * cart-drawer's gift panel (sections/cart-drawer.liquid:1887-1912), so
 * no duplicate config.
 *
 * Dismiss falls back to the cart-drawer gift panel (which auto-opens
 * after ATC; modal sits on top of the drawer).
 */
(function () {
  'use strict';
  if (window.__kitGiftModalLoaded) return;
  window.__kitGiftModalLoaded = true;

  var stylesInjected = false;
  var activeModal = null;
  var claimInFlight = false;

  function injectStyles() {
    if (stylesInjected) return;
    stylesInjected = true;
    var style = document.createElement('style');
    style.textContent = [
      '.kgm-host { position: fixed; inset: 0; z-index: 10000; display: flex; align-items: center; justify-content: center; padding: 16px; opacity: 0; transition: opacity 200ms ease; pointer-events: none; }',
      '.kgm-host[data-visible="true"] { opacity: 1; pointer-events: auto; }',
      '.kgm-backdrop { display: block !important; position: absolute; inset: 0; background: rgba(0, 0, 0, 0.5); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px); }',
      '.kgm-card { position: relative; z-index: 1; background: rgb(var(--color-background, 255 255 255)); color: rgb(var(--color-foreground, 0 0 0)); font-family: var(--font-body-family, system-ui, -apple-system, sans-serif); padding: 32px 32px 24px; border-radius: 12px; max-width: 640px; width: calc(100% - 32px); max-height: calc(100vh - 32px); overflow: hidden; box-shadow: 0 16px 56px rgba(0,0,0,0.28); display: flex; flex-direction: column; gap: 14px; transform: scale(0.96); transition: transform 200ms ease; }',
      '.kgm-host[data-visible="true"] .kgm-card { transform: scale(1); }',
      /* Mobile: bottom-sheet — full width, anchored to bottom, slides up. */
      '@media (max-width: 640px) {',
      '  .kgm-host { align-items: flex-end; justify-content: stretch; padding: 0; }',
      '  .kgm-card { width: 100%; max-width: 100%; max-height: 80vh; border-radius: 16px 16px 0 0; padding: 28px 20px 24px; transform: translateY(100%); }',
      '  .kgm-host[data-visible="true"] .kgm-card { transform: translateY(0); }',
      '}',
      '.kgm-close { position: absolute; top: 10px; right: 10px; width: 36px; height: 36px; background: transparent; border: 0; cursor: pointer; font-size: 26px; line-height: 1; color: inherit; opacity: 0.6; padding: 0; }',
      '.kgm-close:hover { opacity: 1; }',
      '.kgm-title { font-size: 20px; font-weight: 600; margin: 0; padding-right: 32px; }',
      '.kgm-description { font-size: 14px; margin: 0; opacity: 0.7; }',
      '.kgm-slots { display: flex; flex-direction: column; gap: 14px; flex: 1 1 auto; min-height: 0; overflow-y: auto; margin: 0 -8px; padding: 2px 8px; }',
      '.kgm-slot { padding: 14px; border: 1px solid rgba(0, 0, 0, 0.1); border-radius: 8px; display: flex; flex-direction: column; gap: 8px; }',
      '.kgm-slot-label { font-size: 11px; font-weight: 600; margin: 0; text-transform: uppercase; letter-spacing: 0.06em; opacity: 0.6; }',
      '.kgm-slot-row { display: flex; align-items: center; gap: 12px; }',
      '.kgm-slot-thumb { width: 56px; height: 56px; min-width: 56px; border-radius: 6px; overflow: hidden; background: rgba(0,0,0,0.06); display: flex; align-items: center; justify-content: center; }',
      '.kgm-slot-thumb img { width: 100%; height: 100%; object-fit: cover; }',
      '.kgm-slot-thumb span { font-size: 10px; text-align: center; padding: 4px; }',
      '.kgm-slot-thumb.is-sold-out { opacity: 0.4; }',
      '.kgm-size-select { flex: 1; min-width: 0; font: inherit; font-size: 15px; padding: 12px 14px; border: 1px solid rgba(0, 0, 0, 0.25); border-radius: 6px; background: rgb(var(--color-background, 255 255 255)); color: inherit; cursor: pointer; appearance: auto; max-width: 100%; }',
      '.cart-gift-thumb.is-sold-out { opacity: 0.4; cursor: default; }',
      '.cart-gift-thumb.is-sold-out::after { content: ""; position: absolute; inset: 0; background: linear-gradient(to top right, transparent 47%, currentColor 47%, currentColor 53%, transparent 53%); pointer-events: none; }',
      '.cart-gift-thumb { position: relative; }',
      '.kgm-claim { background: rgb(var(--color-foreground, 0 0 0)); color: rgb(var(--color-background, 255 255 255)); border: 0; padding: 16px 20px; border-radius: 8px; cursor: pointer; font-size: 15px; font-weight: 600; letter-spacing: 0.02em; width: 100%; margin-top: 6px; flex-shrink: 0; }',
      '.kgm-claim:disabled { opacity: 0.55; cursor: not-allowed; }',
      '.kgm-claim.is-sold-out { background: rgb(var(--color-foreground, 0 0 0) / 0.5); }',
      '@media (prefers-reduced-motion: reduce) { .kgm-host, .kgm-card { transition: none; } }'
    ].join('\n');
    document.head.appendChild(style);
  }

  function readRules() {
    var el = document.getElementById('CartGiftRules');
    if (!el) return [];
    try { return JSON.parse(el.textContent) || []; } catch (e) { return []; }
  }

  function readVariantMap() {
    var el = document.getElementById('CartGiftVariantMap');
    if (!el) return {};
    try { return JSON.parse(el.textContent) || {}; } catch (e) { return {}; }
  }

  function parseIdList(src) {
    return String(src || '')
      .split(',')
      .map(function (s) { return Number(String(s).trim()); })
      .filter(Boolean);
  }

  function resolveOption(opt, variantMap) {
    var vid = opt.variant_id ? Number(opt.variant_id) : null;
    var label = opt.label || '';
    var image = '';
    var available = true;
    var prod = opt.product ? variantMap[opt.product] : null;
    if (prod) {
      var vname = (opt.variant || '').trim();
      prod.variants.forEach(function (v) {
        if (vid && Number(v.id) === vid) {
          image = v.image; available = v.available !== false; if (!label) label = v.title;
        } else if (!vid && vname && v.options.indexOf(vname) !== -1) {
          vid = Number(v.id); image = v.image; available = v.available !== false; if (!label) label = v.title;
        } else if (!vid && !vname && !image) {
          vid = Number(v.id); image = v.image; available = v.available !== false; if (!label) label = v.title;
        }
      });
      if (!label && prod.title) label = prod.title;
    }
    return vid ? { vid: vid, label: label, image: image, available: available } : null;
  }

  // Modal triggers on ATC for any rule with show_modal_on_atc: true
  // whose trigger_variant_ids (or conditions.items as fallback)
  // includes the variant just added. Independent of per_qualifying_unit,
  // which only controls the slot-count math.
  function findMatchingModalRule(addedVid) {
    var rules = readRules();
    for (var i = 0; i < rules.length; i++) {
      var r = rules[i];
      if (!r.show_modal_on_atc) continue;
      var triggerSrc = r.trigger_variant_ids != null
        ? r.trigger_variant_ids
        : (r.conditions || {}).items;
      var triggerIds = parseIdList(triggerSrc);
      if (triggerIds.length === 0) continue;
      if (triggerIds.indexOf(Number(addedVid)) !== -1) return r;
    }
    return null;
  }

  function computeSlotsRemaining(rule, cart, variantMap) {
    var giftVids = (rule.options || [])
      .map(function (opt) { var r = resolveOption(opt, variantMap); return r ? r.vid : null; })
      .filter(function (v) { return v != null; });
    var giftQty = (cart.items || []).reduce(function (s, i) {
      return giftVids.indexOf(Number(i.variant_id)) !== -1 ? s + i.quantity : s;
    }, 0);

    if (rule.per_qualifying_unit) {
      // Qty-aware: one slot per qualifying trigger item, minus what's
      // already in cart.
      var triggerSrc = rule.trigger_variant_ids != null
        ? rule.trigger_variant_ids
        : (rule.conditions || {}).items;
      var triggerIds = parseIdList(triggerSrc);
      var triggerQty = (cart.items || []).reduce(function (s, i) {
        return triggerIds.indexOf(Number(i.variant_id)) !== -1 ? s + i.quantity : s;
      }, 0);
      return Math.max(0, triggerQty - giftQty);
    }
    // Single-gift rule: show one slot unless any option variant is
    // already in the cart.
    return giftQty > 0 ? 0 : 1;
  }

  function setSlotSelection(slot, opt) {
    slot.setAttribute('data-selected-vid', String(opt.vid));
    slot.setAttribute('data-selected-label', opt.label);
    slot.setAttribute('data-selected-available', opt.available ? '1' : '0');
  }

  // Default labels — override per-rule via rule.claim_button_text
  // (singular) and rule.claim_button_text_multi (with [count] placeholder).
  function defaultClaimLabel(rule, slotCount) {
    var multi = slotCount > 1;
    if (multi && rule && rule.claim_button_text_multi) {
      return String(rule.claim_button_text_multi).replace(/\[count\]/g, String(slotCount));
    }
    if (!multi && rule && rule.claim_button_text) {
      return String(rule.claim_button_text);
    }
    return multi ? 'Claim ' + slotCount + ' Gifts' : 'Claim Gift';
  }

  function refreshClaimButton(host, rule) {
    var btn = host.querySelector('.kgm-claim');
    if (!btn) return;
    var slots = host.querySelectorAll('.kgm-slot');
    var anySoldOut = Array.from(slots).some(function (s) {
      return s.getAttribute('data-selected-available') === '0';
    });
    if (anySoldOut) {
      btn.disabled = true;
      btn.classList.add('is-sold-out');
      btn.textContent = 'Sold out';
    } else {
      btn.disabled = false;
      btn.classList.remove('is-sold-out');
      btn.textContent = defaultClaimLabel(rule, slots.length);
    }
  }

  function buildSlot(index, total, resolved, rule) {
    var slot = document.createElement('div');
    slot.className = 'kgm-slot';
    slot.setAttribute('data-slot-index', String(index));

    // Prefer the first available option as the default selection.
    var defaultOpt = resolved.find(function (o) { return o.available; }) || resolved[0];
    setSlotSelection(slot, defaultOpt);

    if (total > 1) {
      var label = document.createElement('p');
      label.className = 'kgm-slot-label';
      label.textContent = 'Gift ' + (index + 1) + ' of ' + total;
      slot.appendChild(label);
    }

    if (rule && rule.picker_style === 'dropdown') {
      var row = document.createElement('div');
      row.className = 'kgm-slot-row';

      var thumb = document.createElement('div');
      thumb.className = 'kgm-slot-thumb';
      var renderThumb = function (opt) {
        thumb.innerHTML = '';
        thumb.classList.toggle('is-sold-out', !opt.available);
        if (opt.image) {
          var img = document.createElement('img');
          img.src = opt.image;
          img.alt = opt.label;
          img.loading = 'lazy';
          img.width = 56;
          img.height = 56;
          thumb.appendChild(img);
        } else {
          var span = document.createElement('span');
          span.textContent = opt.label;
          thumb.appendChild(span);
        }
      };
      renderThumb(defaultOpt);

      var select = document.createElement('select');
      select.className = 'kgm-size-select';
      select.setAttribute('aria-label', 'Choose size for gift ' + (index + 1));
      resolved.forEach(function (opt) {
        var option = document.createElement('option');
        option.value = String(opt.vid);
        option.textContent = opt.label + (opt.available ? '' : ' — Sold out');
        if (!opt.available) option.disabled = true;
        if (opt.vid === defaultOpt.vid) option.selected = true;
        select.appendChild(option);
      });
      select.addEventListener('change', function () {
        var opt = resolved.find(function (o) { return String(o.vid) === select.value; });
        if (!opt) return;
        setSlotSelection(slot, opt);
        renderThumb(opt);
        var host = slot.closest('.kgm-host');
        if (host) refreshClaimButton(host, rule);
      });

      row.appendChild(thumb);
      row.appendChild(select);
      slot.appendChild(row);
      return slot;
    }

    // Default: thumbnails
    var thumbWrap = document.createElement('div');
    thumbWrap.className = 'cart-gift-thumbnails';

    var selectedLine = document.createElement('p');
    selectedLine.className = 'cart-gift-panel__selected';
    selectedLine.textContent = 'Selected: ' + defaultOpt.label + (defaultOpt.available ? '' : ' (Sold out)');

    resolved.forEach(function (opt) {
      var btn = document.createElement('button');
      btn.type = 'button';
      var isDefault = opt.vid === defaultOpt.vid;
      btn.className = 'cart-gift-thumb'
        + (isDefault ? ' is-active' : '')
        + (!opt.available ? ' is-sold-out' : '');
      btn.setAttribute('data-variant-id', String(opt.vid));
      btn.setAttribute('data-label', opt.label);
      if (!opt.available) btn.title = 'Sold out';
      btn.addEventListener('click', function () {
        thumbWrap.querySelectorAll('.cart-gift-thumb').forEach(function (t) {
          t.classList.toggle('is-active', t === btn);
        });
        selectedLine.textContent = 'Selected: ' + opt.label + (opt.available ? '' : ' (Sold out)');
        setSlotSelection(slot, opt);
        var host = slot.closest('.kgm-host');
        if (host) refreshClaimButton(host, rule);
      });
      if (opt.image) {
        var img = document.createElement('img');
        img.src = opt.image;
        img.alt = opt.label;
        img.loading = 'lazy';
        img.width = 56;
        img.height = 56;
        btn.appendChild(img);
      } else {
        var span = document.createElement('span');
        span.style.cssText = 'display:flex;align-items:center;justify-content:center;height:100%;text-align:center;padding:4px;font-size:10px;';
        span.textContent = opt.label;
        btn.appendChild(span);
      }
      thumbWrap.appendChild(btn);
    });

    slot.appendChild(thumbWrap);
    slot.appendChild(selectedLine);
    return slot;
  }

  function escHandler(e) {
    if (e.key === 'Escape' && activeModal) closeModal();
  }

  function closeModal() {
    if (!activeModal) return;
    document.removeEventListener('keydown', escHandler);
    var host = activeModal;
    activeModal = null;
    host.setAttribute('data-visible', 'false');
    setTimeout(function () {
      if (host.parentNode) host.parentNode.removeChild(host);
    }, 250);
  }

  function renderModal(rule, slotCount, variantMap) {
    injectStyles();
    closeModal();

    var resolved = (rule.options || [])
      .map(function (opt) { return resolveOption(opt, variantMap); })
      .filter(function (o) { return o != null; });
    if (resolved.length === 0) return;

    var host = document.createElement('div');
    host.className = 'kgm-host';
    host.setAttribute('role', 'dialog');
    host.setAttribute('aria-modal', 'true');
    host.setAttribute('aria-labelledby', 'kgm-title');
    host.setAttribute('data-visible', 'false');

    var backdrop = document.createElement('div');
    backdrop.className = 'kgm-backdrop';
    backdrop.addEventListener('click', closeModal);
    host.appendChild(backdrop);

    var card = document.createElement('div');
    card.className = 'kgm-card';

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'kgm-close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '×';
    closeBtn.addEventListener('click', closeModal);
    card.appendChild(closeBtn);

    var title = document.createElement('p');
    title.id = 'kgm-title';
    title.className = 'kgm-title';
    title.textContent = rule.title || 'Claim your free gift';
    card.appendChild(title);

    if (rule.description) {
      var desc = document.createElement('p');
      desc.className = 'kgm-description';
      desc.textContent = rule.description;
      card.appendChild(desc);
    }

    var slotList = document.createElement('div');
    slotList.className = 'kgm-slots';
    for (var i = 0; i < slotCount; i++) {
      slotList.appendChild(buildSlot(i, slotCount, resolved, rule));
    }
    card.appendChild(slotList);

    var claimBtn = document.createElement('button');
    claimBtn.type = 'button';
    claimBtn.className = 'kgm-claim';
    claimBtn.textContent = defaultClaimLabel(rule, slotCount);
    claimBtn.addEventListener('click', function () { handleClaim(host, claimBtn); });
    card.appendChild(claimBtn);

    host.appendChild(card);
    document.body.appendChild(host);
    activeModal = host;
    refreshClaimButton(host, rule);

    requestAnimationFrame(function () { host.setAttribute('data-visible', 'true'); });
    document.addEventListener('keydown', escHandler);
  }

  function handleClaim(host, claimBtn) {
    if (claimInFlight) return;
    claimInFlight = true;
    claimBtn.disabled = true;
    // origText already reflects whatever defaultClaimLabel resolved to
    // for this rule (set by refreshClaimButton at modal open + on every
    // selection change), so we restore to it on error without needing
    // the rule reference here.
    var origText = claimBtn.textContent;
    claimBtn.textContent = 'Adding…';

    var slots = host.querySelectorAll('.kgm-slot');
    var items = [];
    slots.forEach(function (slot) {
      var vid = Number(slot.getAttribute('data-selected-vid'));
      if (vid) items.push({ id: vid, quantity: 1 });
    });

    if (items.length === 0) {
      claimInFlight = false;
      claimBtn.disabled = false;
      claimBtn.textContent = origText;
      return;
    }

    var drawer = document.querySelector('cart-drawer');
    var sectionId = drawer ? drawer.getAttribute('data-section-id') : null;
    var body = { items: items };
    if (sectionId) body.sections = [sectionId];

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart/add.js ' + res.status);
        return res.json();
      })
      .then(function (addResponse) {
        return fetch('/cart.js', { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            if (addResponse && addResponse.sections) cart.sections = addResponse.sections;
            return cart;
          });
      })
      .then(function (cart) {
        if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'kit-gift-modal',
            cart: cart
          });
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
        }
        closeModal();
        if (drawer && typeof drawer.show === 'function') drawer.show();
      })
      .catch(function () {
        claimBtn.textContent = 'Try again';
        setTimeout(function () {
          claimBtn.textContent = origText;
          claimBtn.disabled = false;
        }, 2000);
      })
      .then(function () { claimInFlight = false; });
  }

  document.addEventListener('ajaxProduct:added', function (e) {
    try {
      var detail = e.detail || {};
      var product = detail.product || {};
      var addedVid = Number(product.variant_id || product.id);
      if (!addedVid) return;

      var rule = findMatchingModalRule(addedVid);
      if (!rule) return;

      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var variantMap = readVariantMap();
          var slots = computeSlotsRemaining(rule, cart, variantMap);
          if (slots > 0) renderModal(rule, slots, variantMap);
        })
        .catch(function () {});
    } catch (err) {}
  });
})();
